import './upsell.css';

import EventEmitter from "eventemitter3";
import { IDataCategory } from "../../../models/data.interface";
import { Util } from "../../../utils/util";
import { Data } from '../../../models/data';
import { EventTypes } from '../../../core/event-bus';
import { Pages } from '../../../types/enums';
import { IOrderItem } from '../../../models/order.interface';
import { ArrowScroller } from '../ArrowScroller/arrow-scroller';
import { Context } from '../../../models/context';
import { DataLayerService } from '../../../services/data-layer-service';
import { LocalizationService } from '../../../services/localization-service';

/**
 * Represents the upsell feature that suggests additional items to the user.
 */
export class Upsell {
    private readonly _eventBus: EventEmitter;
    private readonly _context: Context;
    private readonly _localizationService: LocalizationService;
    private readonly _data: Data;
    private readonly _dataLayer: DataLayerService;
    private readonly _containerElement: HTMLElement;
    private readonly _arrowScroller: ArrowScroller;
    private _upsellCategories: IDataCategory[] = [];

    /**
     * Initializes a new instance of the Upsell feature.
     * @param {EventEmitter} eventBus - The event bus for handling events.
     */
    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, dataLayer: DataLayerService) {
        this._eventBus = eventBus;
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;
        this._dataLayer = dataLayer;

        this._containerElement = document.createElement("div");
        this._containerElement.classList.add("upsell-wrapper");

        this._arrowScroller = new ArrowScroller(this._containerElement, { localizationService: this._localizationService });
    }

    /**
     * Gets the main container element of the Upsell.
     * @returns {HTMLElement} The Upsell container element.
     */
    public get element(): HTMLElement {
        return this._arrowScroller.element;
    }

    /**
     * Initializes the upsell section by identifying relevant upsell categories and rendering them.
     * @param {IDataCategory[]} categories - The available menu categories.
     * @param {IOrderItem[]} orderItems - The items currently in the user's cart.
     */
    public init(categories: IDataCategory[], orderItems: IOrderItem[]): void {
        this._filterUpsellCategories(categories, orderItems);
        this._renderUpsellItems();
    }

    /**
     * Checks if there are upsell items available.
     * @returns {boolean} - `true` if upsell items exist, otherwise `false`.
     */
    public hasUpsell(): boolean {
        return this._upsellCategories.length > 0;
    }

    /**
     * Filters out upsell categories that are not already in the user's cart.
     * @param {IDataCategory[]} categories - The available menu categories.
     * @param {IOrderItem[]} orderItems - The items currently in the user's cart.
     */
    private _filterUpsellCategories(categories: IDataCategory[], orderItems: IOrderItem[]): void {
        this._upsellCategories = categories.filter(
            category => category.Upsale && !this._isCategoryInCart(orderItems, category.ItemWebCategoryId)
        );
    }

    /**
     * Checks if a given category is already present in the cart.
     * @param {IOrderItem[]} orderItems - The current cart items.
     * @param {number} categoryId - The ID of the category to check.
     * @returns {boolean} - `true` if the category exists in the cart, otherwise `false`.
     */
    private _isCategoryInCart(orderItems: IOrderItem[], categoryId: number): boolean {
        return orderItems.some(item => item.ItemWebCategoryId === categoryId);
    }

    /**
     * Renders the upsell section on the page.
     */
    private _renderUpsellItems(): void {
        this._containerElement.innerHTML = "";

        for (const category of this._upsellCategories) {
            const element = this._createUpsellCard(category);
            this._containerElement.appendChild(element);
        }
    }

    /**
     * Creates an upsell card for a given category.
     * @param {IDataCategory} category - The category data used to generate the upsell card.
     * @returns {HTMLElement} - The generated upsell card element.
     */
    private _createUpsellCard(category: IDataCategory): HTMLElement {
        const name = this._localizationService.GetName(category.Names);
        const html = `
            <div id="div_upsell_item_${category.ItemWebCategoryId}" class="upsell-card" role="button" tabindex="0" aria-label="View details for ${name}">
                <div class="upsell-card-image">
                    ${category.ImageUrl && category.ImageUrl.trim() !== "" ? `<img src="${category.ImageUrl}" alt="${name}">` : ""}
                </div>
                <div class="upsell-card-label" ltagj="${Util.toLtagj(category.Names)}">${name}</div>
            </div>
        `;

        const element = Util.createHtmlElementFromTemplate(html);
        element.onclick = () => {
            this._context.setProperty("upsellSelected", true);
            this._navigateToUpsellCategory(category.ItemWebCategoryId);
        }

        return element;
    }

    /**
     * Handles navigation to a specific upsell category in the menu.
     * @param {number} webCategoryId - The ID of the web category to navigate to.
     */
    private readonly _navigateToUpsellCategory = (webCategoryId: number): void => {
        this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.MENU);
        this._eventBus.emit(EventTypes.MENU_ANCHOR, webCategoryId);

        if (this._dataLayer) {
            const itemNames = this._data.getProperty("Categories").find(x => x.ItemWebCategoryId === webCategoryId)?.Names ?? [];
            const itemName = this._localizationService.GetName(itemNames);
            const widgetDetail = document.getElementById("div_cart_upsell")?.firstElementChild?.textContent ?? '';
            this._dataLayer.select_upsell(itemName, widgetDetail);
        }
    }
}